import { OnSuccess } from 'src/app/shared/decorators/on-success.decorator';
import { AttachmentService } from 'src/app/shared/attachments/service/attachments.service';
import { State, Action, StateContext, Selector, StateToken } from '@ngxs/store';
import { Injectable } from '@angular/core';
import { tap } from 'rxjs/operators';
import { IComment } from '../model/comment.model';
import { CommentsApiService } from '../services/comments-api.service';
import {
  LoadComments,
  AddComment,
  UpdateComment,
  DeleteComment,
  ClearComments,
} from './comments.actions';

export interface CommentsStateModel {
  comments: IComment[];
  commentsForPopup: IComment[];
}

const COMMENTS_STATE_TOKEN = new StateToken<CommentsStateModel>('comments');

@State<CommentsStateModel>({
  name: COMMENTS_STATE_TOKEN,
  defaults: {
    comments: [],
    commentsForPopup: [],
  },
})
@Injectable()
export class CommentsState {
  constructor(private api: CommentsApiService) {}

  @Action(LoadComments)
  loadComments(
    ctx: StateContext<CommentsStateModel>,
    { entityType, entityId, isInPopup }: LoadComments
  ) {
    return this.api.getComments(entityType, entityId).pipe(
      tap((comments) => {
        const mappedComments = this.mapCommentAttachments(comments);
        ctx.patchState(
          isInPopup ? { commentsForPopup: mappedComments } : { comments: mappedComments }
        );
      })
    );
  }

  @Action(AddComment)
  @OnSuccess({ message: 'commentAdded' })
  addComment(
    ctx: StateContext<CommentsStateModel>,
    { entityType, comment, isInPopup }: AddComment
  ) {
    return this.api.addComment(entityType, comment).pipe(
      tap(() => {
        ctx.dispatch(new LoadComments(entityType, comment.entityId, !!isInPopup));
      })
    );
  }

  @Action(UpdateComment)
  @OnSuccess({ message: 'commentUpdated' })
  updateComment(
    ctx: StateContext<CommentsStateModel>,
    { entityType, id, comment, isInPopup }: UpdateComment
  ) {
    return this.api.updateComment(entityType, id, comment).pipe(
      tap(() => {
        ctx.dispatch(new LoadComments(entityType, comment.entityId, !!isInPopup));
      })
    );
  }

  @Action(DeleteComment)
  @OnSuccess({ message: 'commentDeleted' })
  deleteComment(
    ctx: StateContext<CommentsStateModel>,
    { entityType, entityId, id, isInPopup }: DeleteComment
  ) {
    return this.api.deleteComment(entityType, entityId, id).pipe(
      tap(() => {
        const state = ctx.getState();
        const comments = (isInPopup ? state.commentsForPopup : state.comments).filter(
          (c) => c.id !== id
        );
        ctx.patchState(isInPopup ? { commentsForPopup: comments } : { comments });
      })
    );
  }

  @Action(ClearComments)
  clearComments(ctx: StateContext<CommentsStateModel>) {
    ctx.patchState({ comments: [] });
  }

  @Selector()
  static getComments(state: CommentsStateModel) {
    return state.comments;
  }

  @Selector()
  static getCommentsForPopup(state: CommentsStateModel) {
    return state.commentsForPopup;
  }

  private mapCommentAttachments(comments: IComment[]): IComment[] {
    return comments.map((comment) => ({
      ...comment,
      attachments: AttachmentService.mapAttachments(comment.attachments ?? []),
    }));
  }
}
